export interface TableOfContentsItem {
    id: string;
    title: string;
    level: number;
    element: Element;
}

export function generateTableOfContents(htmlContent: string): TableOfContentsItem[] {
    if (!htmlContent) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    const tocItems: TableOfContentsItem[] = [];
    
    const allElements = doc.querySelectorAll('*');

    allElements.forEach((element, index) => {
        const isHeading = isHeadingElement(element);
        
        if (isHeading) {
            const title = element.textContent?.trim() || '';
            const level = getHeadingLevel(element);
            const id = generateAnchorId(title, index);

            element.id = id;
            
            tocItems.push({
                id,
                title,
                level,
                element: element.cloneNode(true) as Element
            });
        }
    });
    
    return tocItems;
}

function isHeadingElement(element: Element): boolean {
    if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(element.tagName)) {
        return true;
    }
    
    const classList = element.classList;
    
    // Sprawdź nowe klasy CSS
    if (classList.contains('rich-text-h1') || 
        classList.contains('rich-text-h2') ||
        classList.contains('rich-text-h3') ||
        classList.contains('rich-text-h4') ||
        classList.contains('rich-text-h5') ||
        classList.contains('rich-text-h6')) {
        return true;
    }
    
    // Zachowaj kompatybilność ze starymi klasami
    if (classList.contains('font-semibold') &&
        classList.contains('text-[22px]') && 
        classList.contains('leading-[26px]')) {
        return true;
    }
    
    return classList.contains('font-semibold') &&
        classList.contains('text-lg') &&
        classList.contains('leading-[22px]');
}

function getHeadingLevel(element: Element): number {
    if (element.tagName.startsWith('H')) {
        return parseInt(element.tagName.charAt(1));
    }
    
    // Sprawdź nowe klasy CSS
    if (element.classList.contains('rich-text-h1')) {
        return 1;
    }
    
    if (element.classList.contains('rich-text-h2')) {
        return 2;
    }
    
    if (element.classList.contains('rich-text-h3')) {
        return 3;
    }
    
    if (element.classList.contains('rich-text-h4')) {
        return 4;
    }
    
    if (element.classList.contains('rich-text-h5')) {
        return 5;
    }
    
    if (element.classList.contains('rich-text-h6')) {
        return 6;
    }
    
    // Zachowaj kompatybilność ze starymi klasami
    if (element.classList.contains('text-[22px]')) {
        return 1;
    }
    
    if (element.classList.contains('text-lg')) {
        return 2;
    }
    
    return 1;
}

function generateAnchorId(title: string, index: number): string {
    const cleanTitle = title
        .toLowerCase()
        .replace(/[ąćęłńóśźż]/g, (char) => {
            const map: { [key: string]: string } = {
                'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
                'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z'
            };
            return map[char] || char;
        })
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '');
    
    return `heading-${index}-${cleanTitle}`;
}

export function addAnchorsToContent(htmlContent: string): string {
    if (!htmlContent) return htmlContent;
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    const allElements = doc.querySelectorAll('*');
    
    allElements.forEach((element, index) => {
        if (isHeadingElement(element)) {
            const title = element.textContent?.trim() || '';
            element.id = generateAnchorId(title, index);
        }
    });
    
    return doc.body.innerHTML;
}
